import { formatDistanceToNow, isValid } from "date-fns";
import { apiUrl } from "@/api/api";

/**
 * Format a date string to a readable format
 */
export const formatDate = (
  dateString: string | Date,
  options?: Intl.DateTimeFormatOptions,
) => {
  if (!dateString) return "";
  console.debug("options", options);
  let date: Date;
  try {
    // Handle string dates
    if (typeof dateString === "string") {
      // Handle dates with space separator and no timezone info by assuming they're UTC
      if (
        dateString.includes(" ") &&
        !dateString.includes("+") &&
        !dateString.includes("Z")
      ) {
        // Replace space with 'T' and add 'Z' to explicitly mark as UTC
        const formattedDateString = dateString.replace(" ", "T") + "Z";
        date = new Date(formattedDateString);
      } else {
        // For ISO8601 strings (with T and timezone info)
        date = new Date(dateString);
      }
    } else {
      date = dateString;
    }
  } catch (e) {
    console.error("Error parsing date:", e);
    return "Invalid date";
  }

  if (!isValid(date)) return "Invalid date";

  try {
    // For very recent dates (within the last day), use relative time
    const now = new Date();
    const diffInHours = Math.abs(now.getTime() - date.getTime()) / 36e5;

    if (diffInHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true });
    }

    // For dates older than a day, use native toLocaleString which handles timezone conversion
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return String(dateString);
  }
};

/**
 * Format numbers for display (e.g., 1500 -> 1.5K)
 */
export const formatNumber = (num: number): string => {
  if (num === null || num === undefined) return "0";

  if (num === 0) return "0";

  if (num < 1000) return num.toString();

  if (num < 1000000) return (num / 1000).toFixed(1) + "K";

  return (num / 1000000).toFixed(1) + "M";
};

/**
 * Process reply data for display
 */
export const processReplyData = (reply: any) => {
  if (!reply) return null;

  // Extract user details safely
  const userDetails =
    reply.original_message?.user_details ||
    (reply.metadata && reply.metadata.user_details) ||
    {};

  // Handle case where userDetails might be an array or object
  const userProfile =
    Array.isArray(userDetails) && userDetails.length > 0
      ? userDetails[0]
      : typeof userDetails === "object"
        ? userDetails
        : {};

  // Extract basic tweet info
  const tweetId = reply.original_message?.id || "";

  // Safely extract tweet text, handling nested objects
  let tweetText = "";
  if (reply.original_message?.text) {
    if (typeof reply.original_message.text === "object") {
      // If text is an object with its own text property
      if (reply.original_message.text.text) {
        tweetText = reply.original_message.text.text;
      } else {
        // Fallback to stringifying the object
        tweetText = JSON.stringify(reply.original_message.text);
      }
    } else {
      // If text is already a string, use it directly
      tweetText = reply.original_message.text;
    }
  }

  const timestamp = reply.original_message?.timestamp || "";

  // Safely extract user profile data
  const username =
    typeof userProfile.username === "string" ? userProfile.username : "";
  const displayName =
    typeof userProfile.name === "string" ? userProfile.name : "";
  const verified = !!userProfile.verified;
  const location =
    typeof userProfile.location === "string" ? userProfile.location : "";
  const joinedDate = userProfile.created_at || "";
  const profileImage =
    typeof userProfile.profile_image_url === "string"
      ? userProfile.profile_image_url
      : "";

  // Safely process followers count
  let followersCount = 0;
  if (typeof userProfile.followers_count === "number") {
    followersCount = userProfile.followers_count;
  } else if (typeof userProfile.followers_count === "string") {
    const parsed = parseInt(userProfile.followers_count);
    if (!isNaN(parsed)) followersCount = parsed;
  }

  // Extract response data
  const responseText = reply.response_content || "";
  const respondedAt = reply.responded_at || "";
  const responseTweetId = reply.response_tweet_id || "";

  // Safely handle media and thread context
  const mediaUrls = Array.isArray(reply.media_urls) ? reply.media_urls : [];
  const threadContext = Array.isArray(reply.thread_context)
    ? reply.thread_context
    : [];

  return {
    tweetId,
    tweetText,
    timestamp,
    username,
    displayName,
    verified,
    location,
    joinedDate,
    followersCount,
    profileImage,
    responseText,
    respondedAt,
    responseTweetId,
    mediaUrls,
    threadContext,
  };
};

/**
 * Process post data for display
 */
export const processPostData = (post: any) => {
  if (!post) return {};

  // Extract metadata
  const metadata = post.metadata || {};

  // Basic post info
  const postId = post.post_id || metadata.content_id || "";
  const postText = post.text || "";
  const timestamp = post.timestamp || post.created_at || "";

  // Extract media URLs properly
  let mediaUrls = [];

  // Check for media_urls array
  if (post.media_urls && post.media_urls.length > 0) {
    mediaUrls = post.media_urls;
  }
  // Check for GCS URL in metadata
  else if (metadata.gcs_url) {
    mediaUrls = [
      `${apiUrl}/get-file?url=${encodeURIComponent(metadata.gcs_url)}`,
    ];
  }
  // Check for best_media_url in metadata
  else if (metadata.best_media_url) {
    mediaUrls = [metadata.best_media_url];
  }

  // Extract metrics
  const metrics = post.metrics ||
    metadata.metrics || {
      likes: 0,
      retweets: 0,
      replies: 0,
      views: 0,
    };

  return {
    postId,
    postText,
    timestamp,
    mediaUrls,
    metrics,
  };
};
